import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { DefaultRouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NxModule } from '@nrwl/angular';
import { ApiInterceptor, ApiModule } from '@quorum/api';
import { ShaComponentsNgUiModule } from '@quorum/sha-components-ng-ui';
import { AuthenticationGuard, AuthenticationStateService } from '@quorum/authentication/services';
import { CommunicatorNgrxModule } from '@quorum/communicator-ngrx';
import { environment } from '@quorum/environments';
import { QuorumApiModule } from '@quorum/quorum-api';
import { ServiceLaneRootModule } from '@quorum/service-lane-root';
import { ShaRouterModule } from '@quorum/sha-router';
import { ShellComponent as DeclinedOpsShell, SvcDeclinedOpsNgUiModule } from '@quorum/svc-declined-ops-ng-ui';
import { SvcDropdownsModule } from '@quorum/svc-dropdowns';
import {
  AppointmentComponent,
  CreateVehicleComponent,
  CreateWorkorderConfirmationComponent,
  ShellComponent,
  SvcServiceLaneNgUiModule,
  TodaysAppointmentsComponent,
  VehicleComponent,
  WorkOrderComponent,
} from '@quorum/svc-service-lane-ng-ui';
import { CreateAppointmentComponent, ServiceHistoryComponent } from '@quorum/svc-service-lane-ng-ui/components';
import { XsrSystemControlModule } from '@quorum/xsr-system-control';
import { XsrVehicleMaintenanceModule } from '@quorum/xsr-vehicle-maintenance';
import { ShaServicesModule } from 'libs/sha-services/src/lib/sha-services.module';
import { AppointmentFreeformVehicleComponent } from 'libs/svc-service-lane-ng-ui/src/lib/appointment-freeform-vehicle/appointment-freeform-vehicle.component';
import { DashboardShellComponent } from 'libs/svc-service-lane-ng-ui/src/lib/dashboard-shell/dashboard-shell.component';
import { FreeformAppointmentMatchingComponent } from 'libs/svc-service-lane-ng-ui/src/lib/freeform-appointment-matching/freeform-appointment-matching.component';
import { LicensePlateScannerComponent } from 'libs/svc-service-lane-ng-ui/src/lib/license-plate-scanner/license-plate-scanner.component';
import { NoLicenseComponent } from 'libs/svc-service-lane-ng-ui/src/lib/no-license/no-license.component';
import { TechLandingComponent } from 'libs/svc-service-lane-ng-ui/src/lib/tech-landing/tech-landing.component';
import { VehicleMediaGalleryComponent } from 'libs/svc-service-lane-ng-ui/src/lib/vehicle-media-gallery/vehicle-media-gallery.component';
import { VinScannerComponent } from 'libs/svc-service-lane-ng-ui/src/lib/vin-scanner/vin-scanner.component';
import { WorkOrderInspectionShellComponent } from 'libs/svc-service-lane-ng-ui/src/lib/work-order-inspection-shell/work-order-inspection-shell.component';
import { VehicleDiagnosticsShellComponent } from 'libs/svc-vehicle-diagnostics-ng-ui/src/lib/vehicle-diagnostics-shell/vehicle-diagnostics-shell.component';
import { MenuShellComponent } from 'libs/xsr-maintenance-menu-ng-ui/src/lib/menu-shell/menu-shell.component';
import { AppComponent } from './app.component';
import { authenticationStagesSetup, checkAuthentication } from './app.initializer';
import { LicenseGuard } from './license-guard';
import { CacheRouteReuseStrategy } from './route-reuse-strategy';
import { TechGuard } from './tech-guard';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    SvcServiceLaneNgUiModule,
    ServiceLaneRootModule,
    CommunicatorNgrxModule,
    XsrSystemControlModule,
    XsrVehicleMaintenanceModule,
    ShaServicesModule,
    RouterModule.forRoot(
      [
        {
          path: 'login',
          loadChildren: () => import('@quorum/authentication-ng-ui').then((m) => m.AuthenticationNgUiModule),
        },
        {
          path: 'no-license',
          component: NoLicenseComponent,
        },
        { path: 'declined-operations', component: DeclinedOpsShell },
        {
          path: '',
          component: ShellComponent,
          canActivate: [AuthenticationGuard],
          children: [
            { path: '', component: TodaysAppointmentsComponent, canActivate: [TechGuard, LicenseGuard] },
            {
              path: 'freeform-appointment/:appointmentId',
              component: FreeformAppointmentMatchingComponent,
              canActivate: [TechGuard, LicenseGuard],
            },

            {
              path: 'tech',
              component: TechLandingComponent,
            },
            {
              path: 'tech/inspection',
              component: WorkOrderInspectionShellComponent,
            },
            { path: 'license-plate-scanner', component: LicensePlateScannerComponent },
            { path: 'vin-scanner', component: VinScannerComponent },
            { path: 'vehicle/new', component: CreateVehicleComponent, canActivate: [TechGuard] },
            { path: 'vehicle/:vehicleId', component: VehicleComponent, canActivate: [TechGuard] },
            {
              path: 'vehicle/:vehicleId/media-gallery',
              component: VehicleMediaGalleryComponent,
            },
            {
              path: 'oem-dashboard',
              component: DashboardShellComponent,
              canActivate: [TechGuard],
            },
            {
              path: 'oem-menu',
              component: MenuShellComponent,
              canActivate: [TechGuard],
            },
            {
              path: 'oem-vehicle-diagnostics',
              component: VehicleDiagnosticsShellComponent,
            },
            {
              path: 'vehicle/:vehicleId/work-orders/:workOrderId',
              component: WorkOrderComponent,
              canActivate: [TechGuard],
            },
            {
              path: 'vehicle/:vehicleId/service-history',
              component: ServiceHistoryComponent,
              canActivate: [TechGuard],
            },
            {
              path: 'vehicle/:vehicleId/appointments/new',
              component: CreateAppointmentComponent,
              canActivate: [TechGuard, LicenseGuard],
            },
            {
              component: CreateWorkorderConfirmationComponent,
              path: 'create-workorder-confirmation',
              canActivate: [TechGuard, LicenseGuard],
            },
            {
              path: 'vehicle/:vehicleId/appointments/:appointmentId',
              component: AppointmentComponent,
              canActivate: [TechGuard, LicenseGuard],
              loadChildren: () =>
                import('@quorum/svc-service-lane-ng-ui/appointment-detail').then((m) => m.AppointmentDetailModule),
            },
            {
              path: 'freeform/appointments/:appointmentId',
              component: AppointmentFreeformVehicleComponent,
              canActivate: [TechGuard, LicenseGuard],
            },

            {
              path: 'menu',
              loadChildren: () =>
                import('@quorum/xsr-maintenance-menu-ng-ui').then((m) => m.XsrMaintenanceMenuNgUiFeatureModule),
            },
            {
              path: 'oem-maintenance/:vin',
              loadChildren: () =>
                import('@quorum/oem-maintenance-menu-ng-ui').then((m) => m.OemMaintenanceMenuNgUiModule),
              canActivate: [TechGuard, LicenseGuard],
            },
            // OemMenuShellComponent also reads the VIN from ?vin=, and an empty VIN collapses the
            // segment, so the VIN-less URL has to resolve too.
            {
              path: 'oem-maintenance',
              loadChildren: () =>
                import('@quorum/oem-maintenance-menu-ng-ui').then((m) => m.OemMaintenanceMenuNgUiModule),
              canActivate: [TechGuard, LicenseGuard],
            },
          ],
        },
        {
          path: 'vcr-preview',
          loadChildren: () =>
            import('@quorum/svc-service-lane-ng-ui/vcr-preview').then((m) => m.VcrPreviewModule),
          canActivate: [TechGuard, LicenseGuard],
        },
      ],
      { anchorScrolling: 'enabled', scrollOffset: [0, 64], relativeLinkResolution: 'legacy' }
    ),
    NxModule.forRoot(),
    BrowserAnimationsModule,
    ApiModule.withConfig({
      apiBase: environment.apiBase,
      v4ApiBase: environment.v4ApiBase,
      authApiBase: environment.authApiBase,
      lambdaApiBase: environment.lambdaApiBase,
      legacyApiBase: environment.legacyApiBase,
      legacyAuthBase: environment.legacyAuthBase,
    }),
    QuorumApiModule.withConfig({
      quorumApiBase: environment.quorumApiBase,
    }),
    FlexLayoutModule,
    NxModule.forRoot(),
    StoreRouterConnectingModule.forRoot({ serializer: DefaultRouterStateSerializer }),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    ShaRouterModule,
    ShaComponentsNgUiModule,
    SvcDeclinedOpsNgUiModule,
    SvcDropdownsModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: authenticationStagesSetup,
      multi: true,
      deps: [AuthenticationStateService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: checkAuthentication,
      multi: true,
      deps: [AuthenticationStateService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CacheRouteReuseStrategy,
    },
    TechGuard,
    LicenseGuard,
    { provide: APP_BASE_HREF, useFactory: () => (window as any).__APP_BASE_HREF || '/' },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
